/** 
 * Author: Hemant Sharma
 * Type: Helper 
 * Objective: To provide helper functions 
 * Associated Route/Usage: Global 
*/ 

import config from "src/config";
import service from "src/services/userManagementService";
import { toast } from "react-toastify";
import pkceChallenge from "pkce-challenge";
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import messages from "../data/messages";

/**
   * Create an array which can be used as options of Dropdown
   * @items : Array of Object
   * @label : String
   * @value : String
   * @otherFields : Array of String
   * @returns : Array of Object
*/
function convertToSelectOptions(items=[], label='label', value='value', otherFields=[]) {
  if(items && items instanceof Array){
    return items.map((item)=>{
      const obj = {
        label : item[label],
        value : item[value]
      }
      if(otherFields.length > 0){
        for (let i = 0; i < otherFields.length; i++)
        {
          obj[otherFields[i]] = (item[otherFields[i]])?item[otherFields[i]]: typeof (item[otherFields[i]]) === "boolean" ? false : null;
        }
      }
      return obj
    })
  } else{
    return [{
      label : "No data to select",
      value : ""
    }];
  }
}

/**
   * Used to filter an Entity fields and returns only reference fields
   * @items : Array of Object
   * @returns : Array of Object
*/
function fetchRelations(items) {
  if(items && items instanceof Array){
    return items.filter((item)=>item.actualType === 'reference')
  } else{
    return [{
      label : "No data to select",
      value : ""
    }];
  }
}

/**
   * Used to filter an Entity fields and returns non reference fields
   * @items : Array of Object
   * @returns : Array of Object
*/
function fetchOptionsWithoutRelations(items) {
  if(items && items instanceof Array){
    return items.filter((item)=>item.actualType !== 'reference')
  } else{
    return [{
      label : "No data to select",
      value : ""
    }];
  }
}

/**
   * Used to Validate a string for Password 
   * Atleast (1 lower char, 1 upper char, 1 special char and 1 number) and min length should be 8 char
   * @value : String
   * @returns : Boolean
*/
function validatePassword (value) {
    const regexp =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()])[A-Za-z\d@$!%*?&#^()|]{8,}$/;
    return regexp.test(value);
};

/**
   * Used to Validate a string for Email 
   * Atleast @ and . should be there 
   * min 3 char should be after @ and min 2 char should be after .
   * @value : String
   * @returns : Boolean
*/  
function validateEmail (email) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};
  
/**
   * Used to Validate a string for URL 
   * Atleast . should be there 
   * min 2 char should be after . and also verify the HTTP and HTTPS
   * @value : String
   * @returns : Boolean
*/  
function isValidUrl(value) {
    const regEx = /^((http(s?)?):\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/;
    return regEx.test(value);
  }
  
/**
   * Used to Validate a string for Name 
   * Atleast 2 char should be there 
   * There should not be any number and special chars
   * @value : String
   * @returns : Boolean
*/  
function isValidName(value) {
    return /^([a-zA-Z ]){3,128}$/i.test(value);
}
  
/**
   * Used to check if any value is exist in Array or not?
   * @searchValue : String
   * @arrayToSearch : Array
   * @returns : Boolean
*/  
function inArray (searchValue, arrayToSearch) {
    var length = arrayToSearch.length;
    for(var i = 0; i < length; i++) {
        if(arrayToSearch[i] === searchValue)
            return true;
    }
    return false;
}
  
/**
   * Used to encrypt a string
   * @text : String
   * @returns : String
*/  
function crypt (text) {
    const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0));
    const byteHex = (n) => ("0" + Number(n).toString(16)).substr(-2);
    const applySaltToChar = (code) => textToChars(config.cryptKey).reduce((a, b) => a ^ b, code);
  
    return text
        .split("")
        .map(textToChars)
        .map(applySaltToChar)
        .map(byteHex)
        .join("");
};
  
/**
   * Used to decrypt a string
   * @text : String
   * @returns : String
*/  
function decrypt (encoded) {
    const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0));
    const applySaltToChar = (code) => textToChars(config.cryptKey).reduce((a, b) => a ^ b, code);
    return encoded
        .match(/.{1,2}/g)
        .map((hex) => parseInt(hex, 16))
        .map(applySaltToChar)
        .map((charCode) => String.fromCharCode(charCode))
        .join("");
};
  
/**
   * Used to Validate a string for Alphanumeric 
   * Only Char and Number should there
   * @value : String
   * @returns : Boolean
*/  
function isAlphanumeric (str) {
    return str.match("^[A-Za-z0-9]+$");
} 
  
/**
   * Used to change the format of timezone offset
   * @val : number
   * @isNegative : Boolean
   * @returns : String
*/  
function changeOffsetFormat (val, isNegative=false) {
    const offsetInt = (isNegative)?(val * -1):val;
    let mins = offsetInt % 60;
    let hours = (offsetInt - mins) / 60;
    hours = hours < 10 ? '0'+hours : hours;
    mins = mins < 10 ? '0'+mins : mins;
    return (hours + ':' + mins)
}
  
/**
   * Used to get the user's current timezone offset
   * @void
   * @returns : String
*/  
function getOffset() {
    const dhks = new Date();
    const offsetDiff = dhks.getTimezoneOffset();
    if(offsetDiff < 1){
        if(offsetDiff === 0){
            return "+00:00";
        } else{
            let final = '+' + changeOffsetFormat(offsetDiff, true);
            return final;
        }    
    } else{
        let final = '-' + changeOffsetFormat(offsetDiff, false);
        return final;
    }
}

function removeIdTokenTimeLeftFromSessionStorage() {
  sessionStorage.removeItem("session_id");
  sessionStorage.removeItem("session_token");
  sessionStorage.removeItem("time_left");
}

function toTitleCase(str) {
  if (!str?.trim()) return ""; // Check for empty/falsy strings
  return str.replace(/\w\S*/g, function (txt) {
    if (txt === "at") return "at";
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

function inputFieldKeyToLabel(key) {
  return key.replace('_', ' ').replace(/\w\S*/g, (word) => { return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase() });
};

function changeUserNameToCharacterForAvatar(name) {
  if(name) {
    if (name.trim().split(" ").length > 1) {
      let findName = name.split(" ").filter((ele) => {
        if(ele.trim() !== "") {
          return ele;
        }
      })
      return {
        children: `${findName[0][0]}${findName[1][0]}`,
      };
    } else {
      let findName = name.split(" ").filter((ele) => {
        if(ele.trim() !== "") {
          return ele;
        }
      })
      return {
        children: `${findName[0][0]}`,
      }
    }
  } else {
    return {
      children: ""
    }
  }
}

function handleAdditionOfExtensionInIconName(path, iconName, extensionToBeAdded) {
  return `${path}/${iconName}.${extensionToBeAdded}`;
}

function fetchLookupField(element, forSlider) {
  if(Array.isArray(element.sourceEntityField) && (element.fieldType === "targetLookup" || forSlider)) {
    const inputArray = [];
    element.sourceEntityField.map((ele) => {
      if(ele.beforeConstant && ele.afterConstant) {
        inputArray.push(' "' + ele.beforeConstant + '"');
        if(ele.label === "meta_data" && ele.user_input && ele.user_input.trim() !== "") {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + '.' + ele.user_input + ']')
        } else {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + ']')
        }
        inputArray.push(' "' + ele.afterConstant + '"');
      } else if(ele.beforeConstant) {
        inputArray.push(' "' + ele.beforeConstant + '"');
        if(ele.label === "meta_data" && ele.user_input && ele.user_input.trim() !== "") {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + '.' + ele.user_input + ']');
        } else {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + ']');
        }
      } else if(ele.afterConstant) {
        if(ele.label === "meta_data" && ele.user_input && ele.user_input.trim() !== "") {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + '.' + ele.user_input + ']')
        } else {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + ']')
        }
        inputArray.push(' "' + ele.afterConstant + '"');
      } else {
        if(ele.label === "meta_data" && ele.user_input && ele.user_input.trim() !== "") {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + '.' + ele.user_input + ']')
        } else {
          inputArray.push(' [' + ele.segment.value + '/' + formatStringByRemovingDoubleQuotes(ele.label) + ']')
        }
      }
    })
    return forSlider ? `${inputArray.join(" ")}` : `${inputArray.join(" ")} [{${element.lookupEntity.label}/${element.lookupValue.label}}]`;
  }
  // TODO - Make the following code structure dynamic
  if(element.sourceEntityField && (element.sourceEntityField.label || (element.sourceEntityField.path))) {
    if(element.lookupValue.label) {
      let parentField = element.sourceEntityField.label ? element.sourceEntityField.label : element.sourceEntityField.path ? element.sourceEntityField.path : ""
      let lookupEntity = element.lookupEntity.label
      let lookupValue = element.lookupValue.label
      return parentField === "meta_data" ? `${parentField}.${element.sourceEntityField.user_input}[{${lookupEntity}/${lookupValue}}]` : `${parentField}[{${lookupEntity}/${lookupValue}}]`
    }
    if(element.lookupValue.lookupValue &&
      element.lookupValue.lookupValue.label
    ) {
      let parentField = element.sourceEntityField.label
      let lookupEntity = element.lookupEntity.label
      let lookupValue = element.lookupValue.sourceEntityField.label
      let nestedLookupOneEntity = element.lookupValue.lookupEntity.label
      let nestedLookupOneField = element.lookupValue.lookupValue.label
      return `${parentField}[{${lookupEntity}/${lookupValue}}, {${nestedLookupOneEntity}/${nestedLookupOneField}}]`
    }
    let parentField = element.sourceEntityField.label
    let lookupEntity = element.lookupEntity.label
    let lookupValue = element.lookupValue.sourceEntityField.label
    let nestedLookupOneEntity = element.lookupValue.lookupEntity.label
    let nestedLookupOneField = element.lookupValue.sourceEntityField.label
    let nestedLookupTwoEntity = element.lookupValue.lookupValue.lookupEntity.label
    let nestedLookupTwoField = element.lookupValue.lookupValue.lookupValue.label
    return `${parentField}[{${lookupEntity}/${lookupValue}}, {${nestedLookupOneEntity}/${nestedLookupOneField}}, {${nestedLookupTwoEntity}/${nestedLookupTwoField}}]`
  }
  if(element && element.lookupField)
    return `${element.lookupValue.value} (${element.lookupField.label}/${element.lookupEntity.label})`;
  return `${element.lookupValue.value} (${element.relatedField.label}/${element.lookupEntity.label})`;
}

function getIfValueField(nestedValue) {
  if(typeof nestedValue === "string")
    return '"' + nestedValue + '"'
  if(nestedValue.path)
    return formatGivenValue(nestedValue.path, "$$", "/")
  return nestedValue.label ? nestedValue.label : '';
}

function fetchConditionalFuncField(element) {
  let checkMetaData = checkMetaDataAndItsValueExistInGivenFieldType(element, "function-conditional");
  let sourceFieldLabel = element.value[0].path ? formatGivenValue(element.value[0].path, "$$", "/") : element.value[0].label
  let nestedValue = element.value[0].ifValue
  let ifValueData = getIfValueField(nestedValue)
  let funcNameAndSrcFieldString = `${element.function.toUpperCase()}(${sourceFieldLabel}`;
  if(checkMetaData.existInSourceField && checkMetaData.existInIfValueField)
    return `${funcNameAndSrcFieldString}.${element.value[0].user_input}, ${ifValueData}.${nestedValue.user_input})`
  if(checkMetaData.existInSourceField)
    return `${funcNameAndSrcFieldString}.${element.value[0].user_input}, ${ifValueData})`
  if(checkMetaData.existInIfValueField)
    return `${funcNameAndSrcFieldString}, ${ifValueData}.${nestedValue.user_input})`
  return ifValueData ? `${funcNameAndSrcFieldString}, ${ifValueData})` : null;
}

function fetchOtherFunctionField(element) {
  const inputArray = [];
  element.value.map((ele) => {
    let isMetaDataAndItsValueExist = checkMetaDataAndItsValueExistInGivenFieldType(ele);
    let elementLabel = ele.path ? formatGivenValue(ele.path, "$$", "/") : ele.label
    let finalString = isMetaDataAndItsValueExist ? ` ${formatStringByRemovingDoubleQuotes(elementLabel)}.` + ele.user_input : ` ${formatStringByRemovingDoubleQuotes(elementLabel)}`
    if(ele.beforeConstant && ele.afterConstant) {
      inputArray.push(' "' + ele.beforeConstant + '"');
      inputArray.push(finalString);
      inputArray.push(' "' + ele.afterConstant + '"');
    } else if(ele.beforeConstant) {
      inputArray.push(' "' + ele.beforeConstant + '"');
      inputArray.push(finalString);
    } else if(ele.afterConstant) {
      inputArray.push(finalString);
      inputArray.push(' "' + ele.afterConstant + '"');
    } else {
      inputArray.push(finalString);
    }
  })
  return `${element.function.toUpperCase()}(${inputArray})`;
}

function formatGivenValue(value, symbolToBeReplaced, newSymbol) {
  return value.includes(symbolToBeReplaced) ? value.replaceAll(symbolToBeReplaced, newSymbol) : value
}

function checkMetaDataAndItsValueExistInGivenFieldType(element, fieldType) {
  switch(fieldType) {
    case "function-conditional": {
      let result = {
        existInSourceField: element.value?.[0]?.label === "meta_data" && element.value?.[0]?.user_input?.trim() !== "",
        existInIfValueField: element.value?.[0]?.ifValue?.label === "meta_data" && element.value?.[0]?.ifValue?.user_input?.trim() !== ""
      }
      return result;
    }

    // Currently this function is not being used for target lookup but in future it will be used
    case "target-lookup":
      return;

    default:
      return element?.label === "meta_data" && element?.user_input?.trim() !== "";
  }
}

const fetchTypecastFuncField = (element) => {
  let funcNameAndSrcFieldString = element.function.toUpperCase();
  let sourceFieldLabel = element.value[0].path ? formatGivenValue(element.value[0].path, "$$", "/") : element.value[0].label
  let typeToBeConverted = toTitleCase(element.typeToBeConverted)
  if(element.value[0].label === "meta_data")
    return `${funcNameAndSrcFieldString}(${sourceFieldLabel}.${element.value[0].user_input}, ${typeToBeConverted})`
  return `${funcNameAndSrcFieldString}(${sourceFieldLabel}, ${typeToBeConverted})`
}

function handleRepresentationOfFieldOnUi(element, forSlider=false) {
  if(forSlider)
    return fetchLookupField(element, forSlider);
  switch(element?.fieldType) {
    case "constant": {
      if(element.path)
        return formatGivenValue(element.path);
      let isElementTypeBool = (element?.type?.value === 'boolean' && typeof element?.value === "object")
      return isElementTypeBool ? `${element.value.label} (constant)` : `${element.value} (constant)`
    }

    case "relation": {
      let string = `${element.parent}.${element.label}`
      if(checkMetaDataAndItsValueExistInGivenFieldType(element))
        return `${string}.${element.user_input}`
      return string;
    }

    case "function":
      return element.function === "conditional" ? fetchConditionalFuncField(element) : 
      element.function === "typecast" ? fetchTypecastFuncField(element) :
      fetchOtherFunctionField(element);

    case "childRelation":
      return `${element.child_relation_entity}/${element.label}`

    case "sourceLookup":
    case "targetLookup":
      return fetchLookupField(element, forSlider);

    default: {
      if(element) {
        if(checkMetaDataAndItsValueExistInGivenFieldType(element))
          return `${element.label}.${element.user_input}`
        let label = element.path ? formatGivenValue(element.path, "$$", "/") : element.label
        if(label)
          return label
        if(typeof element.value === "string")
          return element.value
        return ""
      }
      return ""
    }
  }
}

function formatStringByRemovingDoubleQuotes(string) {
  return string ? string.replace(/^"(.*)"$/, '$1') : string;
}

function handleRemoveUserFromOrganizationApi(id) {
  return service.removeUser({tenantId: id}).then((resp) => {
    if(resp.status === 419) {
      toast.error(resp.message);
    } else {
      return resp;
    }
  })
}

function handleRoleChangeInOrganization(data) {
  return service.updateUserRole(data).then((resp) => {
    if (resp.status === 409) {
      toast.error(resp.message);
    } else if (resp.status === 419) {
      toast.error(resp.message);
    } else {
      return resp;
    }
  })
}

function isInputFieldEmpty(value, isSpaceValid=false) {
  if(isSpaceValid) {
    return !value;
  }
  return value.trim() === "";
}

function checkWhetherRequiredKeyExistsOrNot(keyName, details)  {
  return Object.keys(details.current.location).find((ele) => {return ele === keyName})
}

function checkGivenNameExistsOrNotInGivenString(string, keyName) {
  return string.indexOf(keyName);
}

function handleRevertingBackOfUser() {
  let checkIsUserLoggedIn = checkGivenNameExistsOrNotInGivenString(window.location.href, "home") > -1
  let checkIsLocationProjectRelated = checkGivenNameExistsOrNotInGivenString(window.location.href, "projects") > -1
  let checkIsLocationFlowRelated = checkGivenNameExistsOrNotInGivenString(window.location.href, "flows") > -1
  let checkIsLocationConnectionRelated = checkGivenNameExistsOrNotInGivenString(window.location.href, "connection") > -1 || checkGivenNameExistsOrNotInGivenString(window.location.href, "connections") > -1
  let checkIsLocationFlowRunHistoryRelated = checkGivenNameExistsOrNotInGivenString(window.location.href, "flow-run-history") > -1

  if(checkIsUserLoggedIn) {
    if(checkIsLocationProjectRelated || checkIsLocationFlowRelated) {
      window.location = "/home/projects";
    } else if(checkIsLocationConnectionRelated) {
      window.location = "/home/connections";
    } else if(checkIsLocationFlowRunHistoryRelated) {
      window.location = "/home/flow-run-history";
    } else {
      window.location = "/home";
    }
  } else {
    window.location = config.routes.login
  }
}

function usePKCEGenerator() {
  const [obj, setObj] = useState(null);
  useEffect(() => {
    const fetchCodeChallengeAndVerifier = () => {
      pkceChallenge(128).then((code) => {
        setObj(code);
      });
    }

    fetchCodeChallengeAndVerifier();
  },[])

  return obj;
}

/**
  * Used to check given name is valid or not
  * According to this expression only these characters are considered to be valid: _, @, $, -, numbers, a-z, A-Z
  * Invalid characters: +, =, {, }, [, ], ^, *, #, !, /, ., :, ;, |, \, %, <, >, `, ~, (, ), ?,"", '', ','
*/ 
function isGivenNameValid(name) {
  const regexp = /^[a-zA-Z0-9$@\-_][a-zA-Z0-9$@\-_ ]*[a-zA-Z0-9$@\-_]$/;
  return regexp.test(name);
}

function isGivenDataValidJson(value) {
  try {
    const parsedJson = JSON.parse(value);
    if (typeof parsedJson === "object" && parsedJson !== null && Object.keys(parsedJson).length > 0) {
      return true;
    }
    return false;
  } catch{
    return false;
  }
}

/**
 * Used to check whether given field name is valid or not
 * Valid characters: $$, -, _, a-z, A-Z, numbers, []
 * Invalid characters: +, =, {, }, ^, *, #, !, ., :, ;, |, \, %, <, >, `, ~, (, ), ?,"", '', ',', /, $, $$$ (only 2 dollar signs are allowed)
 */
function isFieldNameValid(value) {
  const regEx = /^(?:[a-zA-Z0-9_-]+)(?:\$\$(?:[a-zA-Z0-9_-]+|\[\]))*$/;
  return regEx.test(value);
}

/**
 * Validates if a given value is a valid number format
 * Supports: integers, decimals, negative numbers, and scientific notation
 * Rejects: incomplete decimals (123., .98), expressions (9+, 123 + 4), letters, etc.
 * @param {string} value - The value to validate
 * @returns {boolean} - True if valid number, false otherwise
 */
function isNumberValid(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const trimmedValue = value.trim();
  if (trimmedValue === '') {
    return false;
  }
  const numberRegex = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;
  return numberRegex.test(trimmedValue);
}

function convertStatusToShowOnUi(status) {
  switch(status) {
    case "empty_source_data": {
      return "No Data At Source";
    }
    case "partial_success": {
      return "Partial Success";
    }

    case "failure-at-destination": {
      return "Failed At Destination";
    }

    case "canceled": {
      return "Canceled";
    }

    default: {
      return status;
    }
  }
}

/** This func is used to fetch the set of events which should be shown in action events dropdown. 
  Inside this func, we are first checking if trigger event is delete & app on destination side is the one for which "delete" action event will work, 
  then we are truncating the action event array using splice & returning ["Delete"].
*/
function handleActionEvents(selectedAppName, triggerEvent, entityName) {
  let actionEvents = ["Create", "Update" , "Upsert", "Delete"];
  if(selectedAppName === "Soap Api") {
    actionEvents.splice(1, 3);
    return actionEvents;
  }
  if(triggerEvent === "delete" && (selectedAppName === "Business Central" || selectedAppName === "Hubspot" || 
    selectedAppName === "Quick books" || selectedAppName === "Salesforce Health Cloud" || selectedAppName === "Salesforce"
  )) {
    actionEvents.splice(0, 3);
    return actionEvents;
  }
  if(selectedAppName === "Business Central" || selectedAppName === "Hubspot" || selectedAppName === "Quick books") return actionEvents;
  if(selectedAppName === "Woo Commerce") {
    if(entityName === "Product variations")
      return actionEvents.splice(1, 1);
    else actionEvents.splice(2, 2);
    return actionEvents;
  }
  if(selectedAppName === "Odoo") return actionEvents;
  actionEvents.splice(3, 1);
  return actionEvents;
}

const formatIntervalTime = (days, minutes) => {
  if (days) return `${days} ${days === 1 ? 'Day' : 'Days'}`
  const hours = Math.floor(minutes / 60), mins = minutes % 60;
  return `${hours ? `${hours} hours ` : ''}${mins ? `${mins} mins` : ''}`.trim();
};

const getHelperText = key => messages.helperTextList[key] ?? '';

const stripHtmlTags = html => html.replace(/<\/?[^>]+(>|$)/g, "");

/**
 * Sanitizes notification markup before it is injected via dangerouslySetInnerHTML.
 * Notification strings are server/Socket.io-sourced and built from user-controlled
 * values (flow / project / user names), so they must be sanitized to prevent stored
 * XSS. Only the <b> tag is allowed (the UI relies on it to bold names); all other
 * tags, attributes, and scripts are stripped.
 */
const sanitizeNotificationHtml = html =>
  DOMPurify.sanitize(html == null ? "" : String(html), { ALLOWED_TAGS: ["b"], ALLOWED_ATTR: [] });

/**
 * True only for safe, same-origin relative paths (e.g. "/home/flows/details/123").
 * Rejects absolute/external URLs, protocol-relative "//host", backslash tricks
 * ("/\\host" or "\\host" — browsers normalize "\" to "/"), and scheme-based values
 * like "javascript:". Used to guard window.location assignments fed by server data.
 */
const isSafeInternalPath = path =>
  typeof path === "string" &&
  path.length > 1 &&
  path[0] === "/" &&        // must be root-relative
  path[1] !== "/" &&        // not protocol-relative "//evil.com"
  !path.includes("\\");     // no backslashes (browsers treat "\" as "/")

/**
 * Returns `path` if it is a safe internal path, otherwise a safe fallback route.
 * Use for any navigation target that originates from server/user-controlled data
 * (e.g. notification.redirection_type) to prevent open redirects.
 */
const toSafeInternalPath = (path, fallback = "/home") =>
  isSafeInternalPath(path) ? path : fallback;

const getFormatedFlowType = (type) => {
  if (type === 'scheduled')
    return 'Interval';
  if (type === 'synchronized')
    return 'Synchronize';
  if (type === 'realtime')
    return 'Real Time';
  if (type === 'ondemand')
    return 'Ondemand';
}

const handleFilters = (appSelectedOnSource) => {
  let operators = [
    {label: "Equal to", value: "="},
    {label: "Not Equal to", value: "!=="},
    {label: "Greater than", value: ">"},
    {label: "Less than", value: "<"},
    {label: "Contains", value: "like"}
  ]
  if(appSelectedOnSource === "Woo Commerce") {
    operators.splice(4, 1)
    return operators;
  }
  return operators;
}

/**
 * PKCE code_verifier rules:
 * Length: 43–128
 * Allowed chars: A-Z a-z 0-9 - . _ ~
 */
function isValidCodeVerifier(verifier) {
  const verifierRegex = /^[A-Za-z0-9\-._~]{43,128}$/;
  return verifierRegex.test(verifier);
}

const parseData = (data) => {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Validates inputs for field filtering operations.
 * 
 * @param {Array} allOptions - The options array to validate
 * @param {Array} mappingArray - The mapping array to validate
 * @returns {Object} Validation result with isValid flag and fallback return value
 */
function validateFieldFilteringInputs(allOptions, mappingArray) {
  if (!Array.isArray(allOptions) || allOptions.length === 0) {
    return { isValid: false, fallbackValue: [] };
  }
  
  if (!Array.isArray(mappingArray)) {
    return { isValid: false, fallbackValue: allOptions };
  }
  
  return { isValid: true, fallbackValue: null };
}

/**
 * Extracts the actual value from a mapping item.
 * Handles nested structures (via mappingKey) and direct value properties.
 * 
 * @param {Object|any} mappingItem - The mapping item to extract value from
 * @param {string} [mappingKey] - Optional key to access nested value (e.g., 'to', 'destination')
 * @returns {any|null} The extracted value, or null if item is invalid
 */
function extractValueFromMappingItem(mappingItem, mappingKey) {
  if (!mappingItem) return null;
  
  const targetItem = mappingKey ? mappingItem[mappingKey] : mappingItem;
  return targetItem?.value ?? targetItem ?? null;
}

/**
 * Collects values that should be excluded from dropdown options.
 * Gathers values from other rows (excluding current row) and explicitly excluded values.
 * 
 * @param {Array} mappingArray - Array of mapping items containing selected values
 * @param {number} [currentRowIndex] - Index of current row to exclude from collection
 * @param {string} [mappingKey] - Key to access nested value in mapping items
 * @param {Array} explicitlyExcludedValues - Values to always exclude (e.g., ['meta_data'])
 * @returns {Set} Set of values that should be excluded from options
 */
function collectExcludedValuesFromOtherRows(mappingArray, currentRowIndex, mappingKey, explicitlyExcludedValues) {
  const excludedValuesSet = new Set(explicitlyExcludedValues);
  
  mappingArray.forEach((mappingItem, rowIndex) => {
    const isCurrentRow = currentRowIndex !== undefined && rowIndex === currentRowIndex;
    if (isCurrentRow) return;
    
    const selectedValue = extractValueFromMappingItem(mappingItem, mappingKey);
    if (selectedValue != null) {
      excludedValuesSet.add(selectedValue);
    }
  });
  
  return excludedValuesSet;
}

/**
 * Gets the selected value for the current row.
 * 
 * @param {Array} mappingArray - Array of mapping items
 * @param {number} [currentRowIndex] - Index of the current row
 * @param {string} [mappingKey] - Key to access nested value in mapping items
 * @returns {any|null} The current row's selected value, or null if not applicable
 */
function getCurrentRowSelectedValue(mappingArray, currentRowIndex, mappingKey) {
  if (currentRowIndex === undefined) return null;
  
  return extractValueFromMappingItem(mappingArray[currentRowIndex], mappingKey);
}

/**
 * Determines if an option should be included in the filtered results.
 * 
 * @param {Object|any} option - The option to evaluate
 * @param {any|null} currentRowSelectedValue - The value selected in the current row
 * @param {Set} excludedValuesSet - Set of values that should be excluded
 * @returns {boolean} True if option should be included, false otherwise
 */
function shouldIncludeOptionInFilteredList(option, currentRowSelectedValue, excludedValuesSet) {
  const optionValue = option?.value ?? option;
  
  // meta_data should always be available (never excluded)
  if (optionValue === "meta_data") {
    return true;
  }
  
  if (currentRowSelectedValue !== null && optionValue === currentRowSelectedValue) {
    return true;
  }
  
  return !excludedValuesSet.has(optionValue);
}

/**
 * Filters dropdown options to prevent duplicate selections across multiple rows.
 * 
 * This function ensures that each row in a field mapping interface only displays
 * options that haven't been selected in other rows, while always preserving the
 * current row's own selection. This prevents users from accidentally selecting
 * the same field multiple times across different mapping rows.
 * 
 * @param {Array} allOptions - Complete list of available dropdown options.
 *   Each option should be an object with a 'value' property, or a primitive value.
 * @param {Array} mappingArray - Array representing the current field mappings.
 *   Each element contains the selected value for a corresponding row.
 * @param {Object} options - Configuration object for filtering behavior.
 * @param {number} [options.currentRowIndex] - Zero-based index of the row being rendered.
 *   When provided, enables per-row filtering mode:
 *   - Excludes values selected in OTHER rows (at different indices)
 *   - Always includes the value selected in THIS row (if any)
 *   When undefined, performs global filtering (excludes all selected values).
 * @param {string} [options.mappingKey] - Property name to access the selected value within each mapping item.
 *   Use this when mapping items have nested structures, e.g.:
 *   - 'to' for items like: {from: {...}, to: {value: 'field1'}}
 *   - 'destination' for items like: {source: {...}, destination: {value: 'field2'}}
 *   If omitted, assumes mappingArray items directly contain a 'value' property.
 * @param {Array} [options.excludeValues] - List of values to always exclude from options,
 *   regardless of whether they appear in the mapping array.
 *   Useful for excluding system fields like ['meta_data'].
 * @returns {Array} Filtered array of options safe to display in the dropdown.
 *   Empty array if allOptions is invalid or empty.
 * 
 * @example
 * // Per-row filtering: Each row shows only unselected fields
 * const rowOptions = getFilteredFieldOptions(
 *   availableFields,        // All possible fields
 *   fieldMappingArray,      // Current selections: [{to: {value: 'field1'}}, {to: {value: 'field2'}}]
 *   { 
 *     currentRowIndex: 0,   // Filtering for row 0
 *     mappingKey: 'to'      // Access value via item.to.value
 *   }
 * );
 * // Result: Shows all fields except 'field2' (selected in row 1), but includes 'field1' (row 0's selection)
 * 
 * @example
 * // Global filtering: Exclude all selected values plus specific ones
 * const filteredOptions = getFilteredFieldOptions(
 *   destinationFields,
 *   selectedDestinations,   // [{value: 'field1'}, {value: 'field2'}]
 *   { excludeValues: ['meta_data'] }
 * );
 * // Result: Shows all fields except 'field1', 'field2', and 'meta_data'
 */
function getFilteredFieldOptions(allOptions, mappingArray, options = {}) {
  const { currentRowIndex, mappingKey, excludeValues = [] } = options;
  
  const validation = validateFieldFilteringInputs(allOptions, mappingArray);
  if (!validation.isValid) {
    return validation.fallbackValue;
  }

  const excludedValuesSet = collectExcludedValuesFromOtherRows(
    mappingArray,
    currentRowIndex,
    mappingKey,
    excludeValues
  );

  const currentRowSelectedValue = getCurrentRowSelectedValue(
    mappingArray,
    currentRowIndex,
    mappingKey
  );

  return allOptions.filter(option => 
    shouldIncludeOptionInFilteredList(option, currentRowSelectedValue, excludedValuesSet)
  );
}

export { 
  convertToSelectOptions,
  fetchRelations,
  fetchOptionsWithoutRelations,
  validatePassword, 
  validateEmail, 
  isValidUrl, 
  isValidName,
  inArray, 
  crypt, 
  decrypt,
  isAlphanumeric,
  getOffset,
  removeIdTokenTimeLeftFromSessionStorage,
  toTitleCase,
  inputFieldKeyToLabel,
  changeUserNameToCharacterForAvatar,
  handleAdditionOfExtensionInIconName,
  handleRepresentationOfFieldOnUi,
  handleRemoveUserFromOrganizationApi,
  handleRoleChangeInOrganization,
  formatStringByRemovingDoubleQuotes,
  isInputFieldEmpty,
  checkWhetherRequiredKeyExistsOrNot,
  checkGivenNameExistsOrNotInGivenString,
  handleRevertingBackOfUser,
  usePKCEGenerator,
  isGivenNameValid,
  isGivenDataValidJson,
  isFieldNameValid,
  isNumberValid,
  convertStatusToShowOnUi,
  handleActionEvents,
  getHelperText,
  formatIntervalTime,
  stripHtmlTags,
  sanitizeNotificationHtml,
  isSafeInternalPath,
  toSafeInternalPath,
  getFormatedFlowType,
  handleFilters,
  isValidCodeVerifier,
  parseData,
  getFilteredFieldOptions
};